import React, { useEffect, useRef, useState } from "react";
import { theme } from "antd";
import useWindowWidth from "../../hooks/useWindowWidth";
import useHls from "../../hooks/useHls";
import "./VideoPreview.scss";

/**
 * Auto-playing, muted, looping video preview for product cards.
 *
 * - Desktop: plays on hover, rewinds on leave.
 * - Mobile: plays while ≥50% visible in the viewport (IntersectionObserver).
 *
 * Falls back to the poster (thumbnail) frame while paused, and shows a small
 * "Video" badge plus a bottom progress strip mirroring playback position.
 */
export default function VideoPreview({
  hlsUrl,
  thumbnailUrl,
  ariaLabel = "",
  className = "",
}) {
  const { useToken } = theme;
  const { token } = useToken();
  const { isMobile } = useWindowWidth();

  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const [isVisible, setIsVisible] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [progress, setProgress] = useState(0); // 0..100

  useHls(videoRef, hlsUrl);

  // Mobile: autoplay while the card is at least half on-screen.
  useEffect(() => {
    if (!isMobile || !containerRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) =>
          setIsVisible(entry.isIntersecting && entry.intersectionRatio >= 0.5)
        );
      },
      { threshold: [0, 0.5, 1] }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isMobile]);

  // Drive play/pause from the active trigger for the current device.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const shouldPlay = isMobile ? isVisible : hovering;
    if (shouldPlay) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isMobile, isVisible, hovering]);

  // Mirror playback position onto the bottom progress strip.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onTime = () => {
      if (video.duration && Number.isFinite(video.duration)) {
        setProgress(Math.min(100, (video.currentTime / video.duration) * 100));
      }
    };
    const onEnded = () => setProgress(100);
    const onLoaded = () => setProgress(0);
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("ended", onEnded);
    video.addEventListener("loadedmetadata", onLoaded);
    return () => {
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("loadedmetadata", onLoaded);
    };
  }, []);

  const isPlaying = isMobile ? isVisible : hovering;

  // When playback stops, snap the bar back to 0 instantly instead of
  // animating it backwards (which looks like a rewind scrub).
  useEffect(() => {
    if (!isPlaying) setProgress(0);
  }, [isPlaying]);

  return (
    <div
      ref={containerRef}
      className={`videoPreview ${className}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}>
      <video
        ref={videoRef}
        className="videoPreview__video"
        muted
        playsInline
        loop
        preload="metadata"
        poster={thumbnailUrl || undefined}
        aria-label={ariaLabel}
      />
      <div className="videoPreview__badge">
        <svg
          viewBox="0 0 12 12"
          className="videoPreview__badge__icon"
          style={{ fill: token?.colorPrimary }}
          aria-hidden="true">
          <path d="M2.5 1.5v9l8-4.5-8-4.5z" />
        </svg>
        <span className="videoPreview__badge__text">Video</span>
      </div>
      <div className="videoPreview__progress">
        <div
          className="videoPreview__progress__fill"
          style={{
            width: `${progress}%`,
            backgroundColor: token?.colorPrimary,
            transition: isPlaying ? "width 100ms linear" : "none",
          }}
        />
      </div>
    </div>
  );
}
